import React from 'react';
import { Typography, Button } from 'antd';
import { ShoppingCartOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { addToCart } from '../../store/slices/cartSlice';
import { formatPrice } from '../../utils/formatters';
import { motion } from 'framer-motion';
import { Flame } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectFade, Autoplay, Pagination, Navigation } from 'swiper/modules';
import { animateCartFly } from '../../utils/animations';

import './ProductCarousel.css';

// Import Swiper styles if not already globally imported
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

const { Title } = Typography;

const ProductCarousel = ({ products }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  if (!products || products.length === 0) return null;

  const handleAddToCart = (e, product) => {
    e.stopPropagation();
    animateCartFly(e.clientX, e.clientY);
    dispatch(addToCart(product));
  };

  const handleClick = (id) => {
    navigate(`/products/${id}`);
  };

  return (
    <div className="product-carousel-container">
      <div className="carousel-header" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <div style={{ padding: 12, background: '#fee2e2', borderRadius: 16, color: '#ef4444', boxShadow: '0 8px 16px rgba(239, 68, 68, 0.15)' }}>
          <Flame size={24} />
        </div>
        <Title level={3} style={{ margin: 0, fontWeight: 800, fontFamily: 'Outfit', fontSize: 28, color: '#f8fafc' }}>
          Chegirmadagi mahsulotlar
        </Title>
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <Swiper
          modules={[EffectFade, Autoplay, Pagination, Navigation]}
          effect="fade"
          spaceBetween={30}
          slidesPerView={1}
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          pagination={{ clickable: true, dynamicBullets: true }}
          navigation
          loop={true}
          className="sale-swiper"
        >
          {products.map((product) => {
            const price = parseFloat(product.discount_price || product.price);
            const originalPrice = product.discount_price ? parseFloat(product.price) : null;
            const discount = originalPrice
              ? Math.round(((originalPrice - price) / originalPrice) * 100)
              : null;

            const imageUrl = product.image_urls && product.image_urls.length > 0
              ? `${process.env.REACT_APP_API_BASE_URL}${product.image_urls[0]}`
              : 'https://via.placeholder.com/1200x600?text=No+Image';

            return (
              <SwiperSlide key={product.id}>
                <div 
                  className="carousel-slide" 
                  onClick={() => handleClick(product.id)}
                >
                  <div className="carousel-bg" style={{ backgroundImage: `url('${imageUrl}')` }}></div>
                  <div className="carousel-overlay" />
                  
                  <div className="carousel-content glass-content">
                    <div className="carousel-badges">
                      {discount && (
                        <div style={{ background: '#ef4444', padding: '6px 16px', borderRadius: 20, fontWeight: 800, color: 'white', boxShadow: '0 4px 12px rgba(239,68,68,0.4)', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.9rem', letterSpacing: '0.5px' }}>
                          -{discount}% CHEGIRMA
                        </div>
                      )}
                      {!discount && (
                        <div style={{ background: '#0ea5e9', padding: '6px 16px', borderRadius: 20, fontWeight: 800, color: 'white', boxShadow: '0 4px 12px rgba(14,165,233,0.3)', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.9rem', letterSpacing: '0.5px' }}>
                          SUPER NARX
                        </div>
                      )}
                    </div>
                    
                    <h2 className="carousel-title" style={{ fontFamily: 'Outfit' }}>{product.name}</h2>
                    <p className="carousel-desc">{product.description}</p>
                    
                    <div className="carousel-price-row">
                      <div className="price-info">
                        <span className="current-price text-gradient" style={{ background: 'linear-gradient(to right, #ffffff, #e2e8f0)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                          {formatPrice(price)}
                        </span>
                        {originalPrice && (
                          <span className="original-price">{formatPrice(originalPrice)}</span>
                        )}
                      </div>
                      
                      <Button
                        type="primary"
                        size="large"
                        className="carousel-buy-btn"
                        icon={<ShoppingCartOutlined style={{ fontSize: 20 }} />}
                        onClick={(e) => handleAddToCart(e, product)}
                        disabled={!product.is_available || product.stock_quantity <= 0}
                      >
                        {product.is_available && product.stock_quantity > 0 ? "Savatga qo'shish" : "Mavjud emas"}
                      </Button>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </motion.div>
    </div>
  );
};

export default ProductCarousel;
